
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  ref,
  SetupContext,
  toRefs,
  watch,
  Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Button from 'primevue/button';
import OperatorSelectionBox from '@/components/automated-scenarios/OperatorSelectionBox.vue';
import OperatorDisplayBox from '@/components/automated-scenarios/OperatorDisplayBox.vue';
import Tooltip from 'primevue/tooltip';
import {
  addOperator,
  AutomatedScenarioState as state,
  backOperatorSelect,
  getDisplayUrl,
  OperatorMeta,
  resetDefaultDisplayUrl,
  setCurrentConfigurationForm,
  setDefaultDisplayUrl,
  setPersistence,
  StepsOperatorSelector,
  updateOperatorSelectorStep,
  refreshFlowchart,
  setOperatorData,
  setIsAddingOperator,
  getOperatorParents,
} from '@/composables/AutomatedScenarios';
import ConfigComponents from '@/components/automated-scenarios/forms';
import Metadata, { getComponentMetadata } from '@/components/automated-scenarios/metadata';
import { UserState, hasAccessToFeatures } from '@/composables/User';
import { showToastError } from '@/helpers';
import { TypeCampaignEnum } from '@/types';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { checkIfServiceNeedToBeVerified, getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { getServicesToVerify } from '@/configs/services-parameters';
import { useStore } from '@/store';

export default defineComponent({
  name: 'OperatorSelector',

  components: {
    SpmButton,
    OperatorSelectionBox,
    ...ConfigComponents,
    Button,
    OperatorDisplayBox,
    Accordion,
    AccordionTab,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    signalSave: {
      type: Boolean,
      required: false,
      default: () => false,
    },

    actionAfterSave: {
      type: Object as any,
      required: false,
      default: () => ({}),
    },
  },

  emits: ['save', 'edit', 'reset-action-after-save'],

  setup(props, context: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const { signalSave } = toRefs(props);
    const { actionAfterSave } = toRefs(props);
    const formValidation = ref<any>({});

    const data = ref<any>({});
    const selected = ref<OperatorMeta | null>(null);
    const step = computed(() => state.leftToolbar.operatorSelectorStep);
    const configuringSegment = computed(() => state.configuringSegment);

    const action = ref('none');
    const idShop = UserState.activeShop?.id ?? 0;

    const selectLoading: Ref<Record<string, boolean>> = ref({});

    const add = async () => {
      store.commit('liveEditor/setNeedRevalidation', false);
      const component = selected.value?.component;
      if (!component) {
        return;
      }

      const meta = getComponentMetadata(component);
      if (!meta) {
        return;
      }

      const validationContext = {
        operatorId: state.selectedOperator.operatorId,
        parentIds: getOperatorParents(state.selectedOperator.operatorId),
        scenarioType: state.scenario.type,
      };

      if (meta.PreValidate) {
        const predValidation = await meta.PreValidate(data.value, validationContext);
        if (!predValidation.success) {
          const errorMessage = predValidation.message
            ? t(predValidation.message, predValidation.messageParams || {})
            : t('errorMessages.FORM_ERROR');
          await showToastError(errorMessage, 3000, true);
          return;
        }
      }

      if (meta.Validate) {
        try {
          const validation = await meta.Validate(data.value, validationContext);
          if (!validation.success) {
            formValidation.value = validation.validate;
            const errorMessage = validation.message
              ? t(validation.message, validation.messageParams || {})
              : t('errorMessages.FORM_ERROR');
            await showToastError(errorMessage, 3000, true);
            return;
          }
        } catch (error) {
          console.error(error);
          return;
        }
      }

      if (meta.BeforeInsertData) {
        data.value = meta.BeforeInsertData(data.value);
      }

      addOperator(component, data.value);
      setPersistence(state.selectedOperator.operatorId);
      if (meta.Callback) {
        setIsAddingOperator(true);
        try {
          const result = await meta.Callback(data.value);
          if (result !== undefined && result !== null) {
            data.value = result;
            setOperatorData(state.selectedOperator.operatorId, data.value);
            setPersistence(state.selectedOperator.operatorId);
          }
        } finally {
          setIsAddingOperator(false);
        }
      }
      refreshFlowchart();

      if (actionAfterSave.value && actionAfterSave.value.action) {
        actionAfterSave.value.action();
        context.emit('reset-action-after-save');
      }
    };

    const findNearestParent = (givenOperatorId: any) => {
      // Create a mapping of child operators to their parent operators
      const operatorParentMap: Record<any, any> = {};

      const linkObject = JSON.parse(JSON.stringify(state.scenario.data.links));

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const linkKey in linkObject) {
        const link = linkObject[linkKey];
        const childOperatorId = link.toOperator;
        const parentOperatorId = link.fromOperator;

        if (!operatorParentMap[childOperatorId]) {
          operatorParentMap[childOperatorId] = [];
        }

        operatorParentMap[childOperatorId].push(parentOperatorId);
      }

      // Find the nearest parent operator with "id" equal to "boxvisitpage" using DFS
      function findNearestParentWithId(operatorId: any, nearestParentId: any): any {
        const parentOperators = operatorParentMap[operatorId] || [];

        // eslint-disable-next-line no-restricted-syntax
        for (const parentOperatorId of parentOperators) {
          const parentOperator = state.operators.find((operator) => operator.operatorId === parentOperatorId);

          if (parentOperator && parentOperator.id === 'boxvisitpage') {
            return parentOperatorId;
          }

          // Update the nearestParent if the current parent is closer to the desired "id"
          if (!nearestParentId || parentOperators.indexOf(nearestParentId) > parentOperators.indexOf(parentOperatorId)) {
            // eslint-disable-next-line no-param-reassign
            nearestParentId = parentOperatorId;
          }

          // Recursively check parent's parents until a match is found
          const nearestParentObject = findNearestParentWithId(parentOperatorId, nearestParentId);

          if (nearestParentObject) {
            return nearestParentObject;
          }
        }

        return nearestParentId;
      }

      return findNearestParentWithId(givenOperatorId, null);
    };

    const select = async (item: OperatorMeta, dataValue: any = null) => {
      const meta = getComponentMetadata(item.component);
      if (!meta) {
        return;
      }

      selected.value = item;
      if (dataValue !== null) {
        data.value = dataValue;
      } else {
        data.value = meta.Create();
      }

      selectLoading.value[item.label] = true;

      if (meta.Meta.hasConfiguration) {
        const serviceParameterSuccessAction = async () => {
          await nextTick();
          let defaultDisplayUrl = '';
          if (state.selectedOperator.currentConfigurationForm.id === 'boxdisplayfreepopup' || state.selectedOperator.currentConfigurationForm.id === 'boxdisplayfreemodel') {
            const parentOperatorId = state.selectedOperator.meta.id === 'boxvisitpage' ? state.selectedOperator.operatorId : findNearestParent(state.selectedOperator.operatorId);
            if (parentOperatorId) {
              const parent = state.scenario.data.operators[parentOperatorId];

              const getProductOrCategoryOrManufacturer = async (pluralType: string, singularType: string) => {
                const customData = parent.custom;

                if (customData && customData[pluralType] && customData[pluralType].all === 0) {
                  const selection = customData[pluralType][`product_picker_${singularType}`].selection.selected[0];
                  return getDisplayUrl(idShop, singularType, selection.spmId);
                }
                return null;
              };

              // eslint-disable-next-line prefer-destructuring
              defaultDisplayUrl = (parent.custom && parent.custom.urls && Array.isArray(parent.custom.urls.included) && parent.custom.urls.included.length)
                ? parent.custom.urls.included[0]
                : await getProductOrCategoryOrManufacturer('products', 'product')
                  || await getProductOrCategoryOrManufacturer('categories', 'category')
                  || await getProductOrCategoryOrManufacturer('manufacturers', 'manufacturer')
                  || '';
            }
          }
          setDefaultDisplayUrl(defaultDisplayUrl);
          updateOperatorSelectorStep(StepsOperatorSelector.CONFIGURE);
        };

        const callbackAction = async () => {
          if (checkIfServiceNeedToBeVerified(meta.Meta.id)) {
            const notConfiguredServices = await getNotConfiguredServices(idShop, getServicesToVerify(meta, state.operators));
            if (notConfiguredServices.length) {
              store.commit('general/setNotConfiguredServices', notConfiguredServices);
              store.commit('general/setIsServicesParametersModalVisible', true);
              store.commit('general/setServiceParameterSuccessAction', serviceParameterSuccessAction);
            } else {
              await serviceParameterSuccessAction();
            }
          } else {
            await serviceParameterSuccessAction();
          }
        };

        if (state.scenario.type === TypeCampaignEnum.BULK) {
          const featurePermission = hasAccessToFeatures(meta.Meta.id);
          if (featurePermission && !featurePermission.access) {
            store.commit('general/setIsFeatureUnavailableModalVisible', true);
            store.commit('general/setFeatureUnavailableMinPlan', featurePermission.minimumPlan);
            store.commit('general/setOnAuthorizedClickFunction', callbackAction);
          } else {
            await callbackAction();
          }
        } else {
          await callbackAction();
        }
      } else {
        resetDefaultDisplayUrl();
        add();
      }

      selectLoading.value[item.label] = false;
    };

    const cancel = () => {
      backOperatorSelect(false);
    };

    onBeforeMount(async () => {
      if (state.selectedOperator.currentConfigurationForm !== null && state.leftToolbar.operatorSelectorStep !== StepsOperatorSelector.SELECT) {
        const form = state.selectedOperator.currentConfigurationForm.id;
        const meta = Object.values(Metadata).filter((item) => item?.Meta.id === form);
        if (meta.length) {
          const operatorMeta: OperatorMeta = {
            label: meta[0].Meta.label,
            icon: meta[0].Meta.icon,
            kind: meta[0].Meta.kind,
            component: meta[0].Meta.component,
            disabled: false,
          };
          await select(operatorMeta, state.selectedOperator.currentConfigurationForm);
        }
      } else if (state.scenario.type === TypeCampaignEnum.BULK) {
        if (!state.selectedOperator.possibleOutputs.action.length && !state.selectedOperator.possibleOutputs.filtre.length
          && state.selectedOperator.possibleOutputs.declencheur.length === 1) {
          const meta = Object.values(Metadata).filter((item) => item?.Meta.id === 'boxaudiencesetup');
          if (meta.length) {
            const operatorMeta: OperatorMeta = {
              label: meta[0].Meta.label,
              icon: meta[0].Meta.icon,
              kind: meta[0].Meta.kind,
              component: meta[0].Meta.component,
              disabled: false,
            };
            await select(operatorMeta);
          }
        }
      }
    });

    watch(signalSave, async () => {
      await add();
    });

    watch(() => data.value, () => {
      setCurrentConfigurationForm(data.value);
    }, { deep: true });

    return {
      t,
      state,
      step,
      selected,
      select,
      add,
      cancel,
      StepsOperatorSelector,
      data,
      action,
      idShop,
      configuringSegment,
      formValidation,
      selectLoading,
    };
  },
});
